import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTES, useIsDesktop } from '@/components/gg/chrome'
import { useMarkPosted, useMarkRecorded } from '@/hooks/useRecordings'
import { nativeCamera } from '@/lib/nativeCamera'
import { Practice } from '@/components/teleprompter/Practice'
import { useStories } from '@/hooks/useHeadlines'
import { Prompter, countWords, mmss, readTime, splitParas, type PrompterState } from '@/components/teleprompter/prompter'
import {
  RecordEngine, ME, MAX_ITEMS, MAX_CLIP, CORNERS, LAY_KINDS, clipLen, demoItems, itemFromBlob, itemFromStored, layKindFor, panesFor, toStored, thumbOf,
  type Corner, type Item, type Lay, type LayKind, type MePos, type Panes, type RecMode, type WinSize,
} from '@/components/camera/recordEngine'
import { RecordDial, type DialState, type RingItem } from '@/components/camera/RecordDial'
import { CORNER_NAME, CornerIcon, IC, LAY_INFO, LayIcon } from '@/components/camera/recordIcons'
import { recordStore, pref, setPref, scriptFor, scriptEdited, setOwnScript, type Fit, type StoreEntry, type StoredTake } from '@/components/camera/recordStore'
import { FullSheet, GallerySheet, TakeSheet, TrimSheet } from '@/components/camera/RecordSheets'
import { ScriptEditor } from '@/components/camera/ScriptEditor'
import { KEY_BLUE, KEY_DEFAULT, KEY_GREEN, type KeyOpts } from '@/components/camera/chromaKey'
import type { Story } from '@/types/database'
import '@/styles/record.css'

/**
 * Record (Brian's after/record.html v3.2: v3.1 and the Oct 22 commits, then
 * Oct 29 to Nov 3: the computer's control panel, the switcher-style layouts,
 * the phone's camera wheel, the stand-ins as solid colours).
 *
 * The phone (upright only since Oct 30): the script at the top under the lens,
 * the photo strip under it, the picture, Photos and Record. Every setting is on
 * the camera wheel that folds out of the ring above Record (RecordDial): the
 * modes, the layouts (your spots in green screen), Smooth, Mirror, the
 * countdown, slower and faster.
 *
 * The computer: the picture on the left, a control panel on the right (Mode,
 * Frame, Layout, Photos & clips, You, Script, Camera), Record under the
 * picture. A layout can change during a take, like cutting on a switcher; the
 * frame's shape can't.
 *
 * While recording only the script, the red timer, stop, the × and the Slow and
 * Fast pads show.
 */


type Phase = 'ready' | 'counting' | 'rec'
type SheetState =
  | { kind: 'gallery' }
  | { kind: 'trim'; id: number; long: boolean }
  | { kind: 'take'; take: StoredTake }
  | { kind: 'full'; then: (() => void) | null }
  | null

interface Rect { L: number; T: number; W: number; H: number }

const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v))

function loadLay(desk: boolean): Lay {
  try {
    const v = JSON.parse(pref('gogrov.desklay', 'null')) as Partial<Lay> | null
    if (v && v.kind && LAY_KINDS.includes(v.kind)) return { corner: 'br', size: 'm', ...v } as Lay
  } catch { /* */ }
  // the phone starts stacked, the computer side by side
  return { kind: desk ? 'side' : 'stack', corner: 'br', size: 'm' }
}

const LAY_SHORT: Partial<Record<LayKind, string>> = { stack: 'Stacked', full: 'Alone', youwin: 'You small', mediawin: 'Photo small' }
const cap = (w: string) => w[0].toUpperCase() + w.slice(1)

function loadKey(): KeyOpts {
  try { const v = JSON.parse(pref('gogrov.key', 'null')) as Partial<KeyOpts> | null; if (v && v.color) return { ...KEY_DEFAULT, ...v } } catch { /* */ }
  return { ...KEY_DEFAULT }
}

function loadMe(): MePos {
  try { const v = JSON.parse(pref('gogrov.me', 'null')) as MePos | null; if (v && v.s) return v } catch { /* */ }
  return { ...ME.full }
}

export function RecordStudio({ story }: { story: Story }) {
  const desk = useIsDesktop()
  const { data: allStories } = useStories()
  const navigate = useNavigate()
  const markRecorded = useMarkRecorded()
  const markPosted = useMarkPosted()

  // ---------- preferences kept on this device ----------
  const [wpm, setWpmState] = useState(() => clamp(+pref('gogrov.wpm', '130') || 130, 70, 300))
  const [mode, setModeState] = useState<RecMode>(() => (pref('gogrov.recmode', 'split') === 'green' ? 'green' : 'split'))
  const [beauty, setBeautyState] = useState(() => pref('gogrov.beauty', '0') === '1')
  const [mirror, setMirrorState] = useState(() => pref('gogrov.mirror', '1') === '1')
  const [countSecs, setCountSecs] = useState(() => ([3, 5, 10].includes(+pref('gogrov.countdown', '3')) ? +pref('gogrov.countdown', '3') : 3))
  const [tsize, setTsizeState] = useState(() => clamp(+pref('gogrov.tsize', '30') || 30, 24, 40))
  const [me, setMeState] = useState<MePos>(loadMe)
  const [brand, setBrand] = useState(() => ({ f: pref('gogrov.brand.fill', '#c8854a'), r: pref('gogrov.brand.ring', '#ffffff') }))
  // Green cloth: a true chroma key for a real green or blue backdrop (Brian's handoff, log #014)
  const [cloth, setClothState] = useState(() => pref('gogrov.cloth', '0') === '1')
  const [keyOpts, setKeyState] = useState<KeyOpts>(loadKey)
  const [picking, setPickingState] = useState(false)

  // ---------- the frame ----------
  // Oct 30 (Brian): no sideways on the phone; YouTube-size filming is for the computer, Wide 16:9 or Reel 9:16
  const [reel, setReel] = useState(false)
  const land = desk
  const vert = desk ? reel : true
  const [lay, setLayState] = useState<Lay>(() => loadLay(desk))
  const [dialOpen, setDialOpen] = useState(false)

  // ---------- the script ----------
  const [script, setScript] = useState(() => scriptFor(story))
  const paras = useMemo(() => splitParas(script), [script])
  const words = useMemo(() => countWords(script), [script])
  const [editing, setEditing] = useState(false)
  const [editAt, setEditAt] = useState(0)

  // ---------- the take ----------
  const [phase, setPhase] = useState<Phase>('ready')
  const phaseRef = useRef<Phase>('ready')
  const [countN, setCountN] = useState(0)
  const [elapsed, setElapsed] = useState(0)
  const [recMax, setRecMax] = useState(180)
  const [ps, setPs] = useState<PrompterState>({ paused: true, holdPause: false, clipPause: false, m: 1 })
  const [held, setHeld] = useState<-1 | 0 | 1>(0)

  // ---------- the rest ----------
  const [camLive, setCamLive] = useState(false)
  const [gal, setGal] = useState(0) // bumps when the photos and clips change
  const [, setClipVer] = useState(0)
  const [sheet, setSheet] = useState<SheetState>(null)
  const [practice, setPractice] = useState(false)
  const [toastMsg, setToastMsg] = useState<{ node: ReactNode; on: boolean }>({ node: null, on: false })
  const [rect, setRect] = useState<Rect>({ L: 0, T: 0, W: 0, H: 0 })

  const frameRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const bandRef = useRef<HTMLDivElement>(null)
  const txtRef = useRef<HTMLDivElement>(null)
  const readlineRef = useRef<HTMLElement>(null)
  const readdimRef = useRef<HTMLElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)
  const recRef = useRef<HTMLButtonElement>(null)
  const dialState = useRef<DialState | null>(null)
  const engRef = useRef<RecordEngine | null>(null)
  const prompter = useRef(new Prompter()).current
  const t0Ref = useRef(0)
  const lastSecRef = useRef(-1)
  const recMaxRef = useRef(180)
  const cancelCountRef = useRef(false)
  const discardRef = useRef(false)
  const stopRef = useRef<(discard: boolean) => void>(() => undefined)
  const toastT = useRef(0)

  const eng = () => engRef.current
  const items = eng()?.items ?? []
  const cur = eng()?.cur ?? 0
  const curItem: Item | undefined = items[cur]
  void gal

  const toast = useCallback((node: ReactNode, ms = 2600) => {
    setToastMsg({ node, on: true })
    clearTimeout(toastT.current)
    toastT.current = window.setTimeout(() => setToastMsg((t) => ({ ...t, on: false })), ms)
  }, [])

  // ---------- the engine: made once, fed the preferences ----------
  useEffect(() => {
    const canvas = canvasRef.current!
    const e = new RecordEngine(canvas, {
      onTick: (now) => tickRef.current(now),
      onCamera: (live) => setCamLive(live),
      onClip: (playing, ended) => {
        if (playing && phaseRef.current === 'rec') prompter.clipPause = true
        if ((!playing || ended) && prompter.clipPause) prompter.resume()
        setClipVer((v) => v + 1)
      },
      onSeg: (s) => {
        if (s === 'loading') toast('Loading background removal…', 8000)
        else if (s === 'ready') toast('Green screen ready', 1400)
        else toast(<><b>Background removal didn’t load</b> here. In the app the phone does it. Showing the stand-in cut-out.</>, 4200)
      },
    })
    engRef.current = e
    e.mode = mode; e.mirror = mirror; e.beauty = beauty; e.me = me; e.brand = brand; e.lay = lay; e.desk = desk; e.cloth = cloth; e.key = keyOpts
    e.setVertical(vert)
    let gone = false
    const q = new URLSearchParams(location.search)
    // ?demo=1: the solid-colour stand-ins, for boards and checks (never saved)
    if (q.get('demo') === '1') void demoItems().then((d) => { if (gone) { d.forEach((i) => i.stopDemo?.()); return } e.items = d; setGal((g) => g + 1) })
    else void recordStore.loadGallery(story.id).then(async (saved) => {
      for (const s of saved) {
        const it = await itemFromStored(s)
        if (gone) return
        if (it) e.items.push(it)
      }
      e.items = e.items.slice(-MAX_ITEMS)
      setGal((g) => g + 1)
    })
    if (q.get('cam') !== 'off') void e.startCamera()
    return () => { gone = true; e.destroy(); engRef.current = null }
    // the engine is made once per story; the preferences below keep it current
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story.id])

  useEffect(() => { const e = eng(); if (e) { e.setMode(mode) } }, [mode])
  useEffect(() => { const e = eng(); if (e) e.mirror = mirror }, [mirror])
  useEffect(() => { eng()?.setBeauty(beauty) }, [beauty])
  useEffect(() => { const e = eng(); if (e) e.me = me }, [me])
  useEffect(() => { const e = eng(); if (e) e.brand = brand }, [brand])
  useEffect(() => { eng()?.setCloth(cloth) }, [cloth])
  useEffect(() => { const e = eng(); if (e) e.key = keyOpts }, [keyOpts])
  useEffect(() => { const e = eng(); if (e) e.picking = picking }, [picking])
  useEffect(() => { eng()?.setVertical(vert) }, [vert])
  useEffect(() => { const e = eng(); if (e) e.lay = lay }, [lay])
  useEffect(() => { const e = eng(); if (e) e.desk = desk }, [desk])
  useEffect(() => { const e = eng(); if (e) e.taking = phase !== 'ready' }, [phase])

  // The app is upright only (Oct 30); the camera is still told when the interface turns.
  useEffect(() => {
    if (desk || !nativeCamera.available()) return
    let t = 0
    const follow = () => {
      clearTimeout(t)
      t = window.setTimeout(() => eng()?.orient(), 120)
    }
    addEventListener('resize', follow); follow()
    return () => { removeEventListener('resize', follow); clearTimeout(t) }
  }, [desk])

  // ---------- where the overlays sit: measured from the canvas ----------
  const measure = useCallback(() => {
    const fr = frameRef.current?.getBoundingClientRect(), cr = canvasRef.current?.getBoundingClientRect()
    if (!fr || !cr) return
    setRect((r) => {
      const n = { L: cr.left - fr.left, T: cr.top - fr.top, W: cr.width, H: cr.height }
      return Math.abs(n.L - r.L) + Math.abs(n.T - r.T) + Math.abs(n.W - r.W) + Math.abs(n.H - r.H) < 1 ? r : n
    })
    prompter.setOffset(prompter.offset)
  }, [prompter])
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(measure)
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null
    if (ro && stageRef.current) ro.observe(stageRef.current)
    if (ro && bandRef.current) ro.observe(bandRef.current)
    addEventListener('resize', measure)
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); removeEventListener('resize', measure) }
  }, [measure, vert, land, mode])

  // ---------- the teleprompter ----------
  useLayoutEffect(() => {
    prompter.attach(bandRef.current, txtRef.current, readlineRef.current, readdimRef.current)
    prompter.onChange = setPs
  }, [prompter])
  useEffect(() => { prompter.words = words; prompter.wpm = wpm }, [prompter, words, wpm])
  useLayoutEffect(() => { prompter.setOffset(prompter.offset) }, [prompter, tsize, script, land])

  // every drawn frame: the scroll and the timer
  const tickRef = useRef<(now: number) => void>(() => undefined)
  tickRef.current = (now: number) => {
    const on = phaseRef.current === 'rec'
    prompter.tick(now, on)
    if (!on) return
    const dur = (now - t0Ref.current) / 1000
    const sec = Math.floor(dur)
    if (sec !== lastSecRef.current) { lastSecRef.current = sec; setElapsed(dur) }
    if (dur >= recMaxRef.current) stopRef.current(false)
  }

  // ---------- the longest take: the story sets it ----------
  const maxTake = () => {
    const read = (words / wpm) * 60, clips = (eng()?.items ?? []).reduce((a, i) => a + clipLen(i), 0)
    return Math.min(600, Math.max(180, Math.round(read + clips + 60)))
  }

  // ---------- preferences ----------
  const setWpm = (v: number) => { const w = clamp(v, 70, 300); setWpmState(w); setPref('gogrov.wpm', String(w)) }
  const setTsize = (v: number) => { const t = clamp(v, 24, 40); setTsizeState(t); setPref('gogrov.tsize', String(t)) }
  const saveMe = (m: MePos) => { setMeState(m); setPref('gogrov.me', JSON.stringify(m)) }
  const setBeauty = (on: boolean) => { setBeautyState(on); setPref('gogrov.beauty', on ? '1' : '0') }
  const setMirror = (on: boolean) => { setMirrorState(on); setPref('gogrov.mirror', on ? '1' : '0') }
  const setKey = (k: Partial<KeyOpts>) => setKeyState((o) => { const n = { ...o, ...k }; setPref('gogrov.key', JSON.stringify(n)); return n })
  const setPicking = (on: boolean) => {
    setPickingState(on)
    if (on) toast(<><b>{desk ? 'Click' : 'Tap'} your cloth</b> in the picture to pick its colour.</>, 6000)
  }
  /** Green cloth on: the colour behind you is read from the picture's edges when it is a clear green or blue. */
  const setCloth = (on: boolean) => {
    setClothState(on); setPref('gogrov.cloth', on ? '1' : '0')
    if (!on) { setPickingState(false); return }
    const hex = eng()?.autoKey()
    if (hex) { setKey({ color: hex }); toast(<><b>Green cloth on.</b> Keyed to the colour behind you.</>, 2600) }
    else toast(<><b>Green cloth on.</b> No clear green or blue showed round you, so it keys {keyOpts.color === KEY_BLUE ? 'blue' : 'green'}.{desk ? ' Use Pick to choose the colour.' : ''}</>, 3600)
  }
  const nextCount = () => {
    const n = countSecs === 3 ? 5 : countSecs === 5 ? 10 : 3
    setCountSecs(n); setPref('gogrov.countdown', String(n))
    return n
  }
  const cycleCount = () => { const n = nextCount(); toast(<>Countdown: <b>{n} seconds</b></>, 1400) }
  const setLay = (l: Partial<Lay>) => setLayState((o) => { const n = { ...o, ...l }; setPref('gogrov.desklay', JSON.stringify(n)); return n })
  const pickMe = (k: keyof typeof ME) => {
    const m: MePos = { ...ME[k] }
    if (m.c) { m.f = pref('gogrov.cfill', ''); m.r = pref('gogrov.cring', 'white') }
    saveMe(m)
  }
  const setMode = (m: RecMode | 'read') => {
    if (phaseRef.current !== 'ready') return
    if (m === 'read') { setPractice(true); return }
    setModeState(m); setPref('gogrov.recmode', m); setPickingState(false)
    if (m === 'green' && pref('gogrov.gshint', '') !== '1') {
      setPref('gogrov.gshint', '1')
      toast(desk
        ? <><b>Place yourself.</b> Drag yourself to move; scroll on yourself, or use Size, to size yourself. Full, Left, Right and Circle are quick spots.</>
        : <><b>Place yourself.</b> Pinch to size yourself, drag to move. Full, Left and Right are quick spots. Tap a background below to switch.</>, 4200)
    }
  }

  // ---------- the photos and clips ----------
  const saveGallery = useCallback(() => {
    const e = eng(); if (e) void recordStore.saveGallery(story.id, e.items.filter((i) => !i.demo).map(toStored))
  }, [story.id])
  const refreshGallery = () => setGal((g) => g + 1)
  const go = (i: number) => { eng()?.go(i); refreshGallery() }
  const addFiles = async (files: File[]) => {
    const e = eng(); if (!e) return
    if (await recordStore.isFull()) { setSheet({ kind: 'full', then: null }); return }
    let trimFirst: Item | null = null
    for (const f of files) {
      if (e.items.length >= MAX_ITEMS) { toast(<><b>The gallery holds {MAX_ITEMS}.</b> Remove one to add another.</>); break }
      const it = await itemFromBlob(f, f.type.startsWith('video') ? 'video' : 'img', f.name.replace(/\.[^.]+$/, ''))
      if (!it) { toast('That file didn’t open.'); continue }
      if (it.type === 'video' && it.dur > MAX_CLIP && !trimFirst) trimFirst = it
      e.items.push(it)
    }
    e.cur = Math.max(0, e.items.length - 1)
    saveGallery(); refreshGallery()
    if (trimFirst) setSheet({ kind: 'trim', id: trimFirst.id, long: true })
  }
  const removeItem = (i: number) => {
    const e = eng(); if (!e) return
    e.stopClips()
    const [gone] = e.items.splice(i, 1)
    if (gone?.url) URL.revokeObjectURL(gone.url)
    gone?.stopDemo?.()
    if (e.cur >= e.items.length) e.cur = Math.max(0, e.items.length - 1)
    saveGallery(); refreshGallery()
  }
  const pickFiles = () => fileRef.current?.click()
  const onCleared = (x: StoreEntry) => {
    const e = eng()
    if (!e || x.kind !== 'gallery' || x.story !== story.id) return
    e.stopClips(); e.items.forEach((i) => { if (i.url) URL.revokeObjectURL(i.url); i.stopDemo?.() }); e.items = []; e.cur = 0; refreshGallery()
  }

  // ---------- recording ----------
  const start = async () => {
    const e = eng(); if (!e || phaseRef.current !== 'ready') return
    if (await recordStore.isFull()) { setSheet({ kind: 'full', then: () => void start() }); return }
    setSheet(null); setDialOpen(false)
    cancelCountRef.current = false
    phaseRef.current = 'counting'; setPhase('counting')
    e.taking = true
    await e.askMic()
    for (let n = countSecs; n >= 1; n--) {
      setCountN(n)
      await new Promise((r) => setTimeout(r, 1000))
      if (cancelCountRef.current) { phaseRef.current = 'ready'; setPhase('ready'); setCountN(0); e.taking = false; return }
    }
    setCountN(0)
    prompter.reset(); prompter.paused = false
    const max = maxTake(); recMaxRef.current = max; setRecMax(max)
    discardRef.current = false
    e.startRecording()
    t0Ref.current = performance.now(); lastSecRef.current = -1; setElapsed(0)
    phaseRef.current = 'rec'; setPhase('rec')
    e.primeClip(e.items[e.cur])
  }

  const stop = async (discard: boolean) => {
    const e = eng(); if (!e || phaseRef.current !== 'rec') return
    phaseRef.current = 'ready'; setPhase('ready'); e.taking = false
    e.stopClips(); setHeld(0); prompter.pad(0)
    const dur = (performance.now() - t0Ref.current) / 1000
    const poster = e.poster()
    const blob = await e.stopRecording()
    if (discard) { toast(<><b>Take discarded.</b> ✕ at the top leaves Record.</>, 2600); return }
    const take: StoredTake = { story: story.id, at: Date.now(), dur, land: !e.vertical, mode: e.mode, blob, poster, mime: blob?.type || '' }
    const id = await recordStore.addTake(take)
    if (id != null) take.id = id
    setSheet({ kind: 'take', take })
    // a take always saves, even the one that fills the space
    if (await recordStore.isFull()) toast(<><b>Saved.</b> That filled GoGrov’s space on this phone. Clear some before the next take.</>, 4200)
  }
  stopRef.current = (d) => { void stop(d) }

  const onRecTap = () => {
    if (phaseRef.current === 'rec') { void stop(false); return }
    if (phaseRef.current === 'counting') return
    try { eng()?.audioGraph() } catch { /* */ } // made inside the tap: iOS suspends one made outside
    void start()
  }
  const onBail = () => {
    if (phaseRef.current === 'counting') { cancelCountRef.current = true; return }
    if (phaseRef.current === 'rec') void stop(true)
  }

  // ---------- the band: tap to edit before recording; tap / hold / drag while recording ----------
  const bandPtr = useRef({ y0: 0, o0: 0, t0: 0, moved: false, down: false })
  const onBandDown = (ev: React.PointerEvent) => {
    const b = bandPtr.current
    b.down = true; b.y0 = ev.clientY; b.o0 = prompter.offset; b.t0 = performance.now(); b.moved = false
    if (phaseRef.current === 'rec' && !prompter.paused) prompter.holdPause = true
    try { (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId) } catch { /* */ }
  }
  const onBandMove = (ev: React.PointerEvent) => {
    const b = bandPtr.current
    if (!b.down) return
    const dy = ev.clientY - b.y0
    if (Math.abs(dy) > 6) b.moved = true
    if (b.moved) prompter.setOffset(b.o0 - dy)
  }
  const onBandUp = () => {
    const b = bandPtr.current
    if (!b.down) return
    b.down = false
    const quick = performance.now() - b.t0 < 260
    if (prompter.holdPause) {
      prompter.holdPause = false
      if (quick && !b.moved) prompter.pause(); else if (!prompter.paused) prompter.highlightHere()
      return
    }
    if (phaseRef.current !== 'rec') { if (quick && !b.moved && phaseRef.current === 'ready') openEdit(); return }
    if (quick && !b.moved) {
      if (prompter.paused || prompter.clipPause) { prompter.resume(); eng()?.stopClips() } else prompter.pause()
    }
  }

  const openEdit = () => {
    if (phaseRef.current !== 'ready' || editing) return
    setEditAt(prompter.paragraphAt()); setEditing(true)
  }
  const closeEdit = (text: string | null): boolean => {
    if (text != null) {
      const t = text.replace(/\r/g, '').replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim()
      if (!t) { toast('The script can’t be empty. Restore brings back the written one.'); return false }
      setOwnScript(story.id, story.voice, t === (story.script || '').trim() ? null : t)
      setScript(t)
      toast('Script updated')
      requestAnimationFrame(() => prompter.setOffset(Math.min(prompter.offset, txtRef.current?.scrollHeight ?? 0)))
    }
    setEditing(false)
    return true
  }

  // ---------- the pads ----------
  const padDown = (dir: -1 | 1) => (ev: React.PointerEvent) => {
    ev.preventDefault(); ev.stopPropagation()
    if (phaseRef.current !== 'rec') return
    setHeld(dir); prompter.pad(dir)
    try { (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId) } catch { /* */ }
  }
  const padUp = (dir: -1 | 1) => () => { if (held === dir || prompter.target !== 1) { setHeld(0); prompter.pad(0) } }

  // ---------- green screen: pinch and drag yourself, pan and zoom the picture; swipe the pictures ----------
  const gest = useRef<{ pts: Map<number, { x: number; y: number }>; g: null | { kind: 'pinch' | 'drag' | 'bgpinch' | 'bgdrag'; s0?: number; x0?: number; y0?: number; m0?: { x: number; y: number; d: number }; cx?: number; cy?: number; moved: boolean; it?: Item; f0?: Fit }; sx: number | null; sy: number; used: boolean }>({ pts: new Map(), g: null, sx: null, sy: 0, used: false })
  const toCanvas = (cx: number, cy: number) => {
    const r = canvasRef.current!.getBoundingClientRect(), e = eng()!
    return { x: ((cx - r.left) / r.width) * e.W, y: ((cy - r.top) / r.height) * e.H }
  }
  const onMe = (cx: number, cy: number) => {
    const e = eng(); if (!e) return false
    const p = toCanvas(cx, cy), b = e.meBox()
    return p.x > b.dx + b.dw * 0.12 && p.x < b.dx + b.dw * 0.88 && p.y > b.dy + b.dh * 0.18 && p.y < b.dy + b.dh
  }
  const mid = () => { const a = [...gest.current.pts.values()]; return { x: (a[0].x + a[1].x) / 2, y: (a[0].y + a[1].y) / 2, d: Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y) } }
  const meRef = useRef(me); meRef.current = me
  const clampMe = (m: MePos): MePos => ({ ...m, s: clamp(m.s, 0.3, 1.25), x: clamp(m.x, 0.12, 0.88), y: clamp(m.y, 0.5, 1.3) })
  const sgT = useRef(0)

  const onStageDown = (ev: React.PointerEvent) => {
    const G = gest.current, e = eng()
    G.sx = ev.clientX; G.sy = ev.clientY; G.used = false
    // the eyedropper: the click picks the key colour and does nothing else
    if (e && picking && mode === 'green') {
      const p = toCanvas(ev.clientX, ev.clientY), hex = e.pickAt(p.x, p.y)
      G.used = true; setPickingState(false)
      if (hex) { setKey({ color: hex }); toast(<><b>Colour picked.</b> Tolerance widens or narrows what is keyed.</>, 2200) }
      else toast(<><b>No camera picture</b> to pick from.</>, 2200)
      return
    }
    if (!e || mode !== 'green' || !(vert || desk)) return // Oct 29 (#015): the mouse does it on the computer too
    G.pts.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    try { stageRef.current?.setPointerCapture(ev.pointerId) } catch { /* */ }
    const bg = e.items[e.cur] ?? null
    if (G.pts.size === 2) {
      const m = mid()
      G.g = onMe(m.x, m.y) || !bg ? { kind: 'pinch', s0: meRef.current.s, x0: meRef.current.x, y0: meRef.current.y, m0: m, moved: true } : { kind: 'bgpinch', it: bg, f0: e.fitOf(bg), m0: m, moved: true }
      G.sx = null
    } else if (G.pts.size === 1 && onMe(ev.clientX, ev.clientY)) G.g = { kind: 'drag', x0: meRef.current.x, y0: meRef.current.y, cx: ev.clientX, cy: ev.clientY, moved: false }
    else if (G.pts.size === 1 && bg) G.g = { kind: 'bgdrag', it: bg, f0: e.fitOf(bg), cx: ev.clientX, cy: ev.clientY, moved: false }
  }
  const onStageMove = (ev: React.PointerEvent) => {
    const G = gest.current, g = G.g, e = eng()
    if (!g || !e || !G.pts.has(ev.pointerId)) return
    G.pts.set(ev.pointerId, { x: ev.clientX, y: ev.clientY })
    const r = canvasRef.current!.getBoundingClientRect()
    if (g.kind === 'pinch' && G.pts.size >= 2) {
      const m = mid()
      const nm = clampMe({ ...meRef.current, s: g.s0! * (m.d / Math.max(20, g.m0!.d)), x: g.x0! + (m.x - g.m0!.x) / r.width, y: g.y0! + (m.y - g.m0!.y) / r.height })
      meRef.current = nm; e.me = nm
    } else if (g.kind === 'drag') {
      const dx = ev.clientX - g.cx!, dy = ev.clientY - g.cy!
      if (Math.hypot(dx, dy) > 6) g.moved = true
      if (g.moved) { const nm = clampMe({ ...meRef.current, x: g.x0! + dx / r.width, y: g.y0! + dy / r.height }); meRef.current = nm; e.me = nm }
    } else if (g.kind === 'bgpinch' && G.pts.size >= 2) {
      const m = mid()
      e.panFit(g.it!, g.f0!, g.f0!.s * (m.d / Math.max(20, g.m0!.d)), (m.x - g.m0!.x) / r.width, (m.y - g.m0!.y) / r.height)
    } else if (g.kind === 'bgdrag') {
      const dx = ev.clientX - g.cx!, dy = ev.clientY - g.cy!
      if (Math.hypot(dx, dy) > 6) g.moved = true
      if (g.moved) e.panFit(g.it!, g.f0!, g.f0!.s, dx / r.width, dy / r.height)
    }
  }
  const onStageUp = (ev: React.PointerEvent) => {
    const G = gest.current, e = eng()
    G.pts.delete(ev.pointerId)
    if (G.g && !G.pts.size) {
      const used = G.g.kind === 'pinch' || G.g.moved, k = G.g.kind
      G.g = null
      if (used) {
        if (k === 'pinch' || k === 'drag') saveMe(meRef.current)
        else { clearTimeout(sgT.current); sgT.current = window.setTimeout(saveGallery, 800) }
        G.sx = null; G.used = true
      }
    }
    if (G.used || G.sx == null || !e) { G.sx = null; return }
    const dx = ev.clientX - G.sx, dy = ev.clientY - G.sy
    G.sx = null
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(e.cur + (dx < 0 ? 1 : -1))
    else if (Math.abs(dx) < 8 && Math.abs(dy) < 8) {
      if (!e.items.length) { if (phaseRef.current === 'ready') setSheet({ kind: 'gallery' }) }
      else if (phaseRef.current === 'rec' && e.items[e.cur].type !== 'img' && mode !== 'green') toggleClip()
      // green screen: a tap on the picture behind you, not on you, moves to the next one, in the same take
      else if (mode === 'green' && e.items.length > 1 && !onMe(ev.clientX, ev.clientY)) go(e.cur + 1)
    }
  }

  // Oct 29 (#015): on the computer, scrolling on yourself in green screen sizes you (the Size slider in the panel does too)
  useEffect(() => {
    const st = stageRef.current
    if (!st || !desk) return
    const onWheel = (ev: WheelEvent) => {
      if (mode !== 'green' || !onMe(ev.clientX, ev.clientY)) return
      ev.preventDefault()
      saveMe(clampMe({ ...meRef.current, s: meRef.current.s * (ev.deltaY < 0 ? 1.04 : 0.96) }))
    }
    st.addEventListener('wheel', onWheel, { passive: false })
    return () => st.removeEventListener('wheel', onWheel)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desk, mode])

  const toggleClip = () => { const e = eng(); if (!e) return; if (e.clipPlaying) e.pauseClip(); else e.playClip() }

  // ---------- keys ----------
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.target as HTMLElement)?.closest?.('input,textarea')) return
      const e = eng(); if (!e) return
      if (ev.key === ' ') {
        ev.preventDefault()
        if (phaseRef.current !== 'rec') return
        if (prompter.paused || prompter.clipPause) { prompter.resume(); e.stopClips() } else prompter.pause()
      } else if (ev.key === 'm' || ev.key === 'M') { if (phaseRef.current === 'rec') { e.setClipMute(!e.clipMuted); setClipVer((v) => v + 1) } }
      else if (ev.key === 'ArrowUp') setWpm(wpmRef.current + 10)
      else if (ev.key === 'ArrowDown') setWpm(wpmRef.current - 10)
      else if (ev.key === 'ArrowRight') go(e.cur + 1)
      else if (ev.key === 'ArrowLeft') go(e.cur - 1)
      else if (ev.key === 'Escape') setSheet((s) => (s && s.kind !== 'take' && s.kind !== 'full' ? null : s))
    }
    addEventListener('keydown', onKey)
    return () => removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prompter])
  const wpmRef = useRef(wpm); wpmRef.current = wpm

  // ---------- leaving ----------
  // ✕ leaves Record: on the phone to the story, on a computer to the feed (Brian's back())
  const close = () => { if (phaseRef.current !== 'ready') return; navigate(desk ? ROUTES.feed : ROUTES.story(story.id)) }
  const onDone = () => {
    markRecorded.mutate({ storyId: story.id })
    navigate(ROUTES.feed)
  }
  const onPosted = async (caption: string) => {
    try { await markPosted.mutateAsync({ storyId: story.id, caption }) } catch (err) { toast(`Couldn’t file it as posted: ${(err as Error).message}`); return }
    navigate(`${ROUTES.library}?f=posted`)
  }

  // ---------- derived ----------
  const busy = phase !== 'ready'
  const pausedNow = (ps.paused || ps.holdPause) && !ps.clipPause
  const left = recMax - elapsed
  const live = Math.abs(ps.m - 1) > 0.03
  const bandState = phase !== 'rec'
    ? (editing || phase === 'counting' ? '' : `${desk ? 'Click' : 'Tap'} to edit · ${readTime(words, wpm)} to read`)
    : ps.clipPause ? 'Clip playing · tap to go on' : pausedNow ? 'Script paused · still recording · tap to go on' : ''
  const isGreen = mode === 'green'
  const stripTx = items.length ? `translateX(${-23 - cur * 54}px)` : 'translateX(-50%)'
  const meKey = (Object.keys(ME) as (keyof typeof ME)[]).find((n) => !!ME[n].c === !!me.c && Math.abs(ME[n].s - me.s) < 0.02 && Math.abs(ME[n].x - me.x) < 0.02 && Math.abs(ME[n].y - me.y) < 0.02)
  const showSnd = !!curItem && curItem.type === 'video'
  const kindNow = layKindFor(lay.kind, vert, desk)
  const winKind = kindNow === 'youwin' || kindNow === 'mediawin'

  // what the frame shows (the engine draws the same): with nothing added, a take has you fill the frame
  const W = vert ? 720 : 1280, H = vert ? 1280 : 720
  const panes: Panes = !items.length && busy ? panesFor('full', W, H) : panesFor(kindNow, W, H, lay.corner, lay.size)
  useEffect(() => {
    if (!import.meta.env.DEV) return
    const w = window as unknown as { __ggRec?: Record<string, unknown> }
    w.__ggRec = { ...(w.__ggRec || {}), panes: () => eng()?.panes(), lay: () => eng()?.lay, dial: () => dialState.current, seg: () => eng()?.segStats(), eng: () => eng() }
  })

  // the photo's pane (the whole frame in green screen): the dots, the prompt and the clip pill sit over it
  const k = rect.W / W || 0
  const M = isGreen ? null : panes.media || panes.you
  const visL = M ? rect.L + M.x * k : rect.L, visT = M ? rect.T + M.y * k : rect.T
  const visW = M ? M.w * k : rect.W, visH = M ? M.h * k : rect.H
  // the band: under the lens at the top of the picture on a computer, no wider than reads well (Oct 29, #015)
  const bw = Math.min(rect.W - 24, 900)
  const bandStyle: CSSProperties = desk ? { left: rect.L + (rect.W - bw) / 2, width: bw, top: rect.T + 12, height: 170 } : {}
  const sndStyle: CSSProperties = desk ? { left: visL + visW - 10, top: visT + 10, transform: 'translateX(-100%)', right: 'auto' } : {}
  const promptOn = desk && !items.length && (isGreen || !!panes.media)

  const trimItem = sheet?.kind === 'trim' ? items.find((i) => i.id === sheet.id) : undefined

  const frameCls = ['rec-frame', land && 'land', isGreen && 'green', isGreen && picking && 'picking', busy && 'rec-hidden', phase === 'rec' && 'pedals', editing && 'editing', desk ? 'desk' : 'dialui', dialOpen && 'dial-open']
    .filter(Boolean).join(' ')

  // ---------- the phone's camera wheel: every setting on one ring ----------
  // Oct 30 (Brian: "the less options the better; feature-rich but not overwhelming and cramped"): eleven buttons, roomy. The
  // photos stay on the strip at the top; the countdown is one button that cycles; a window has a corner (medium size); the circle a fill.
  const ringItems: RingItem[] = desk ? [] : [
    ...([['read', 'Practice', 'Practice'], ['split', 'Split screen', 'Split'], ['green', 'Green screen', 'Green']] as const)
      .map(([m, l, sl]): RingItem => ({ g: 'Mode', k: 'mode:' + m, l, sl, ic: IC[m], on: mode === m, act: () => setMode(m) })),
    ...(isGreen
      ? (['full', 'left', 'right', 'circle'] as const).map((n): RingItem => ({ g: 'You', k: 'me:' + n, l: cap(n), ic: IC[n], on: meKey === n, act: () => pickMe(n) }))
      : (['stack', 'youwin', 'mediawin'] as const).map((n): RingItem => ({ g: 'Layout', k: 'lay:' + n, l: LAY_SHORT[n]!, lay: true, on: kindNow === n,
        ic: <LayIcon kind={n} corner={lay.corner} size={lay.size} tall />, act: () => setLay({ kind: n }) }))),
    { g: 'Camera', k: 'beauty', l: 'Smooth', ic: IC.beauty, on: beauty, act: () => setBeauty(!beauty) },
    { g: 'Camera', k: 'mirror', l: 'Mirror', ic: IC.mirror, on: mirror, act: () => setMirror(!mirror) },
    { g: 'Countdown', k: 'cd', l: `${countSecs} seconds before the take`, sl: `${countSecs}s`, tx: `${countSecs}s`, keep: true, act: () => { nextCount() } },
    { g: 'Speed', k: 'wpm-', l: 'Slower', tx: '−', keep: true, act: () => setWpm(wpm - 10) },
    { g: 'Speed', k: 'wpm+', l: 'Faster', tx: '+', keep: true, act: () => setWpm(wpm + 10) },
  ]
  // the inner arc: a window's corner; the circle's fill
  // green screen: Green cloth first (a real backdrop keyed out), then the circle's fill
  const clothItem: RingItem = { g: 'Cut out', k: 'cloth', l: 'Green cloth', ic: IC.cloth, on: cloth, act: () => setCloth(!cloth) }
  const subItems: RingItem[] = desk ? []
    : !isGreen && winKind ? CORNERS.map((c): RingItem => ({ k: 'win:' + c, l: cap(CORNER_NAME[c]), ic: <CornerIcon c={c} />, on: lay.corner === c, act: () => setLay({ corner: c }) }))
      : isGreen ? [clothItem, ...(me.c ? ([['', 'The camera picture', ''], ['#ffffff', 'White', '#fff'], ['#000000', 'Black', '#000'], ['#c8854a', 'Amber', '#c8854a'], ['brand', 'Your brand colour', brand.f]] as const)
        .map(([v, l, col]): RingItem => ({ k: 'fill:' + (v || 'cam'), l, dot: col, cam: !v, on: (me.f || '') === v, act: () => { saveMe({ ...me, f: v }); setPref('gogrov.cfill', v) } })) : [])]
        : []

  // a one-time hint says where the settings went
  useEffect(() => {
    if (desk || pref('gogrov.dialhint', '') === '1') return
    const t = window.setTimeout(() => {
      setPref('gogrov.dialhint', '1')
      toast(<><b>Your settings are on the wheel.</b> Tap the ring above Record, spin it with a thumb, tap what you want.</>, 4500)
    }, 1200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desk])

  // ---------- pieces shared by the phone and the computer ----------
  const stripEl = (
    <div className={`rec-strip${items.length ? '' : ' empty'}`} onPointerDown={(e) => e.stopPropagation()}>
      <div className="bgs" aria-label="Photos and clips" style={desk ? undefined : { transform: stripTx }}>
        {items.map((it, i) => (
          <button key={it.id} type="button" className={`${i === cur ? 'on' : ''}${it.type !== 'img' ? ' clip' : ''}`} style={{ backgroundImage: it.thumb ? `url(${it.thumb})` : undefined }}
            aria-label={`Background ${i + 1}${it.type !== 'img' ? ', a clip' : ''}`} onClick={() => go(i)} />
        ))}
        {items.length < MAX_ITEMS && (items.length
          ? <button type="button" className="add" aria-label="Add a photo or clip" onClick={pickFiles}>+</button>
          : <button type="button" className="add first" onClick={pickFiles}>＋ Add photos or clips <small>optional</small></button>)}
      </div>
    </div>
  )
  const galCount = items.length ? `${items.length} of ${MAX_ITEMS}` : 'Optional'
  const spdEl = (
    <div className="rec-spd" role="group" aria-label="Reading speed">
      <button type="button" aria-label="Slower" onClick={() => setWpm(wpm - 10)}>−</button>
      <span className="sv"><small>wpm</small><b className={live ? 'live' : ''}>{live ? `${Math.round(wpm * ps.m)} ${ps.m > 1 ? '↑' : '↓'}` : wpm}</b></span>
      <button type="button" aria-label="Faster" onClick={() => setWpm(wpm + 10)}>+</button>
    </div>
  )
  const cfillEl = me.c && (
    <div className="rec-cfill" role="group" aria-label="Behind you in the circle, and its outline">
      <div className="crow"><small>Fill</small>
        <button type="button" aria-label="The camera picture" aria-pressed={!me.f} onClick={() => { saveMe({ ...me, f: '' }); setPref('gogrov.cfill', '') }}>
          <svg viewBox="0 0 24 24"><path d="M4 8h3l2-2.5h6L17 8h3v10H4z" /><circle cx="12" cy="13" r="3" /></svg>
        </button>
        {([['#ffffff', 'White'], ['#000000', 'Black'], ['#c8854a', 'Amber'], ['#5c7a5a', 'Sage'], ['#3b5068', 'Slate']] as const).map(([c, n]) => (
          <button key={c} type="button" aria-label={n} aria-pressed={me.f === c} onClick={() => { saveMe({ ...me, f: c }); setPref('gogrov.cfill', c) }}><i style={{ background: c }} /></button>
        ))}
        <label className="wheelbtn" aria-label="Your brand colour: opens the colour wheel" aria-pressed={me.f === 'brand'}>
          <i className="wheel"><b style={{ background: brand.f }} /></i>
          <input type="color" className="cpick" aria-label="Brand fill colour" value={brand.f}
            onClick={() => { saveMe({ ...me, f: 'brand' }); setPref('gogrov.cfill', 'brand') }}
            onChange={(e) => { const b = { ...brand, f: e.target.value }; setBrand(b); setPref('gogrov.brand.fill', b.f); saveMe({ ...me, f: 'brand' }); setPref('gogrov.cfill', 'brand') }} />
        </label>
      </div>
      <div className="crow"><small>Outline</small>
        <button type="button" aria-label="White outline" aria-pressed={(me.r || 'white') === 'white'} onClick={() => { saveMe({ ...me, r: 'white' }); setPref('gogrov.cring', 'white') }}><i className="ring" /></button>
        <button type="button" aria-label="Outline matches the fill" aria-pressed={me.r === 'match'} onClick={() => { saveMe({ ...me, r: 'match' }); setPref('gogrov.cring', 'match') }}><i className="ring match" /></button>
        <label className="wheelbtn" aria-label="Outline in your brand colour: opens the colour wheel" aria-pressed={me.r === 'brand'}>
          <i className="wheel"><b style={{ background: brand.r }} /></i>
          <input type="color" className="cpick" aria-label="Brand outline colour" value={brand.r}
            onClick={() => { saveMe({ ...me, r: 'brand' }); setPref('gogrov.cring', 'brand') }}
            onChange={(e) => { const b = { ...brand, r: e.target.value }; setBrand(b); setPref('gogrov.brand.ring', b.r); saveMe({ ...me, r: 'brand' }); setPref('gogrov.cring', 'brand') }} />
        </label>
      </div>
    </div>
  )

  // the computer's layouts: an icon per layout (you amber, the photo grey), each window kind at its four corners
  const layDefs: [LayKind, Corner | null][] = [['full', null], ['side', null], ['sidef', null], ['stack', null], ...CORNERS.map((c): [LayKind, Corner] => ['youwin', c]), ...CORNERS.map((c): [LayKind, Corner] => ['mediawin', c])]
  const deskWin = lay.kind === 'youwin' || lay.kind === 'mediawin'
  const layHint = (deskWin ? `${LAY_INFO[lay.kind]} ${CORNER_NAME[lay.corner]}` : vert && /side/.test(lay.kind) ? `${LAY_INFO.stack} (side by side needs the wide frame)` : LAY_INFO[lay.kind])
    + (items.length ? '.' : '. Add photos or clips to fill the other pane.')

  return (
    <div ref={frameRef} className={frameCls} style={{ '--tsize': `${tsize}px` } as CSSProperties} data-gg-file="record.html">
      <div className="rec-stage" ref={stageRef}
        onPointerDown={onStageDown} onPointerMove={onStageMove} onPointerUp={onStageUp} onPointerCancel={onStageUp}>
        <canvas ref={canvasRef} width={720} height={1280} />
      </div>

      {/* ✕ out of Record; the rest of the setup is on the wheel (phone) or in the panel (computer) */}
      <div className="rec-topbar">
        <button className="rec-ib" type="button" aria-label="Close" onClick={close}>
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      </div>

      {/* the script, never recorded */}
      <div ref={bandRef} className={`rec-band${phase === 'rec' && pausedNow ? ' paused' : ''}${phase === 'rec' && ps.clipPause ? ' fade' : ''}`} style={bandStyle}
        onPointerDown={onBandDown} onPointerMove={onBandMove} onPointerUp={onBandUp} onPointerCancel={onBandUp}>
        <i className="read-dim" ref={readdimRef} />
        <i className="readline" ref={readlineRef} />
        <span className={`state${phase === 'rec' && (pausedNow || ps.clipPause) ? ' on' : ''}`}>{bandState}</span>
        <div className="txt" ref={txtRef}>
          {paras.length ? paras.map((p, i) => <p key={i}>{p}</p>) : <p>No script for this story yet. You can still record.</p>}
        </div>
      </div>

      {/* the photos and clips behind you */}
      {desk && items.length > 1 && (
        <div className="rec-dots" style={{ left: visL + visW / 2, top: visT + visH - 18 }}>
          {items.map((it, i) => <i key={it.id} className={`${i === cur ? 'on' : ''}${it.type !== 'img' ? ' clip' : ''}`} />)}
        </div>
      )}
      {promptOn && (
        <button type="button" className="rec-prompt" style={{ left: visL + visW / 2, top: visT + visH / 2 - 18 }}
          onClick={() => { if (phaseRef.current === 'ready') setSheet({ kind: 'gallery' }) }}><span>Add photos or clips</span></button>
      )}
      {!desk && stripEl}
      {items.length > 1 && !desk && (
        <>
          <button className="rec-cyc l" type="button" aria-label="Previous photo or clip" onClick={() => go(cur - 1)}><svg viewBox="0 0 24 24"><path d="M15 5l-7 7 7 7" /></svg></button>
          <button className="rec-cyc r" type="button" aria-label="Next photo or clip" onClick={() => go(cur + 1)}><svg viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg></button>
        </>
      )}
      {showSnd && (
        <div className="rec-sndrow" style={sndStyle}>
          <button className={`rec-snd${curItem.playing ? ' playing' : ''}`} type="button" aria-label={curItem.playing ? 'Pause the clip' : 'Play the clip'}
            onClick={(e) => { e.stopPropagation(); toggleClip() }}>
            <svg viewBox="0 0 24 24">{curItem.playing ? <><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></> : <path d="M7 5v14l12-7z" />}</svg>
            <span>{curItem.playing ? 'Pause clip' : 'Play clip'}</span>
          </button>
        </div>
      )}

      {!camLive && phase === 'ready' && (
        <div className="rec-camnote" style={desk ? { left: rect.L + 10, top: rect.T + rect.H - 30 } : { left: rect.L + 10 }}>
          <span>Camera off · stand-in</span>
          <button type="button" onClick={() => { void eng()?.startCamera().then((ok) => { if (!ok) toast(<><b>No camera.</b> The browser said no, or there isn’t one. The stand-in takes your place.</>, 3600) }) }}>Use my camera</button>
        </div>
      )}

      {/* the red timer capsule; amber with "Script paused"; white 15 s before the end */}
      {phase === 'rec' && (
        <div className={`rec-timer${left <= 15 ? ' warn' : pausedNow ? ' paused' : ''}`}>
          {left <= 15 ? `${mmss(Math.max(0, left))} left` : pausedNow ? `Script paused · ${mmss(elapsed)}` : <>{mmss(elapsed)} <small>/ {mmss(recMax)}</small></>}
        </div>
      )}
      {busy && (
        <button className="rec-bail" type="button" aria-label="Stop and discard this take" onClick={onBail}>
          <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
      )}

      {/* the phone's camera wheel: the folded ring above Record, and the wheel that fans out of it */}
      {!desk && (
        <RecordDial open={dialOpen && !busy} show={!busy} items={ringItems} sub={subItems} wpm={wpm} green={isGreen}
          onOpen={() => { if (phaseRef.current === 'ready') setDialOpen(true) }} onClose={() => setDialOpen(false)}
          frameRef={frameRef} recRef={recRef} stateRef={dialState} />
      )}

      <div className="rec-bottom">
        {!desk && (
          <button className="rec-gal" type="button" onClick={() => setSheet({ kind: 'gallery' })}>
            <span className={`th${curItem?.thumb ? '' : ' none'}`} style={{ backgroundImage: curItem?.thumb ? `url(${curItem.thumb})` : undefined }} />
            <span>Photos<b>{galCount}</b></span>
          </button>
        )}
        <button ref={recRef} className={`rec-rec${phase === 'rec' ? ' on' : ''}${phase === 'counting' ? ' counting' : ''}`} type="button" aria-label={phase === 'rec' ? 'Stop' : 'Record'}
          style={{ '--cd': `${countSecs}s` } as CSSProperties} onClick={onRecTap}>
          <i />
          <svg className="cring" viewBox="0 0 96 96" aria-hidden="true"><circle cx="48" cy="48" r="46" /></svg>
        </button>
      </div>

      {/* Oct 29 (#015): the computer's control panel */}
      {desk && (
        <aside className="rec-panel" onPointerDown={(e) => e.stopPropagation()}>
          <div className="grp"><small>Mode</small>
            <div className="rec-seg" role="group" aria-label="Mode">
              <button type="button" aria-pressed={false} onClick={() => setMode('read')}>Practice</button>
              <button type="button" aria-pressed={mode === 'split'} onClick={() => setMode('split')}>Split screen</button>
              <button type="button" aria-pressed={mode === 'green'} onClick={() => setMode('green')}>Green screen</button>
            </div>
          </div>
          <div className="grp"><small>Frame</small>
            <div className="rec-seg" role="group" aria-label="The frame you record">
              <button type="button" aria-pressed={!reel} aria-label="Record wide, 16:9, the full width" onClick={() => { if (!busy) setReel(false) }}>Wide 16:9</button>
              <button type="button" aria-pressed={reel} aria-label="Record a vertical reel, 9:16, cropped from the centre" onClick={() => { if (!busy) setReel(true) }}>Reel 9:16</button>
            </div>
          </div>
          {!isGreen && (
            <div className="grp live"><small>Layout</small>
              <div className={`rec-lays${vert ? ' reel' : ''}`} role="group" aria-label="Where you and the photo sit">
                {layDefs.map(([kd, c]) => {
                  const on = lay.kind === kd && (!c || lay.corner === c), off = vert && (kd === 'side' || kd === 'sidef')
                  const label = c ? `${kd === 'youwin' ? 'You in a window' : 'The photo in a window'}, ${CORNER_NAME[c]}` : LAY_INFO[kd]
                  return (
                    <button key={kd + (c || '')} type="button" aria-pressed={on} aria-label={label} title={label} disabled={off}
                      onClick={() => setLay(c ? { kind: kd, corner: c } : { kind: kd })}>
                      <LayIcon kind={kd} corner={c || lay.corner} size={lay.size} tall={vert} />
                    </button>
                  )
                })}
              </div>
              <p className="hint">{layHint}</p>
              {deskWin && (
                <div className="ctl"><span className="hint lab">Window</span>
                  <div className="rec-seg" role="group" aria-label="Window size">
                    {([['s', 'Small'], ['m', 'Medium'], ['l', 'Large']] as [WinSize, string][]).map(([z, n]) => (
                      <button key={z} type="button" aria-pressed={lay.size === z} onClick={() => setLay({ size: z })}>{n}</button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="grp live"><small>Photos &amp; clips</small>
            {stripEl}
            <button className="rec-gal" type="button" onClick={() => setSheet({ kind: 'gallery' })}><span>Manage<b>{galCount}</b></span></button>
          </div>
          {isGreen && (
            <div className="grp live"><small>You</small>
              <div className="rec-mepos" role="group" aria-label="Where you stand">
                {(['full', 'left', 'right', 'circle'] as const).map((n) => (
                  <button key={n} type="button" aria-pressed={meKey === n} onClick={() => pickMe(n)}>{cap(n)}</button>
                ))}
              </div>
              {cfillEl}
              <div className="ctl"><span className="hint lab">Size</span>
                <input type="range" className="sz" min={30} max={125} value={Math.round(me.s * 100)} aria-label="Your size"
                  onChange={(e) => saveMe(clampMe({ ...me, s: +e.target.value / 100 }))} />
              </div>
              <p className="hint">Drag yourself on the picture to move; scroll on yourself to size. In a circle, the frame follows your face.</p>
            </div>
          )}
          {isGreen && (
            <div className="grp live"><small>Cut out</small>
              <div className="rec-mepos" role="group" aria-label="How you are cut out">
                <button type="button" aria-pressed={!cloth} onClick={() => setCloth(false)}>Automatic</button>
                <button type="button" aria-pressed={cloth} onClick={() => setCloth(true)}>Green cloth</button>
              </div>
              {cloth ? (
                <>
                  <div className="rec-cfill rec-key" role="group" aria-label="The colour behind you">
                    <div className="crow"><small>Colour</small>
                      <button type="button" aria-label="Green cloth" aria-pressed={keyOpts.color === KEY_GREEN} onClick={() => setKey({ color: KEY_GREEN })}><i style={{ background: KEY_GREEN }} /></button>
                      <button type="button" aria-label="Blue cloth" aria-pressed={keyOpts.color === KEY_BLUE} onClick={() => setKey({ color: KEY_BLUE })}><i style={{ background: KEY_BLUE }} /></button>
                      <label className="wheelbtn" aria-label="The key colour: opens the colour wheel" aria-pressed={keyOpts.color !== KEY_GREEN && keyOpts.color !== KEY_BLUE}>
                        <i style={{ background: keyOpts.color }} />
                        <input type="color" className="cpick" aria-label="Key colour" value={keyOpts.color} onChange={(e) => setKey({ color: e.target.value })} />
                      </label>
                      <button type="button" className="pick" aria-label="Pick the colour from the picture" aria-pressed={picking} onClick={() => setPicking(!picking)}>
                        <svg viewBox="0 0 24 24"><path d="M14.5 5.5l4 4M17 3.5l3.5 3.5-2.5 2.5-3.5-3.5zM14.6 6.9L6 15.5V18h2.5l8.6-8.6" /></svg>
                        <span>Pick</span>
                      </button>
                    </div>
                  </div>
                  {([['tol', 'Tolerance', 'Tolerance: how far from the colour still counts as the cloth'], ['soft', 'Softness', 'Softness: how soft your edges are'],
                    ['spill', 'Spill', 'Spill suppression: takes the green cast off your edges']] as const).map(([kk, n, al]) => (
                    <div className="ctl" key={kk}><span className="hint lab">{n}</span>
                      <input type="range" className="sz" min={0} max={100} value={keyOpts[kk]} aria-label={al} onChange={(e) => setKey({ [kk]: +e.target.value })} />
                    </div>
                  ))}
                  <p className="hint">{picking ? 'Click the cloth in the picture.' : 'For a real green or blue cloth behind you, lit evenly. Pick, then click the cloth in the picture.'}</p>
                </>
              ) : (
                <p className="hint">The computer finds you and cuts out the room. Have a green or blue cloth behind you? Green cloth keys it out cleanly.</p>
              )}
            </div>
          )}
          <div className="grp"><small>Script</small>
            <div className="ctl">
              {spdEl}
              <button className="rec-cdn" type="button" aria-label={`Countdown: ${countSecs} seconds. Click to change.`} onClick={cycleCount}>{countSecs}s</button>
            </div>
          </div>
          <div className="grp"><small>Camera</small>
            <div className="ctl">
              <button className="rec-ib lab" type="button" aria-label="Smooth skin" aria-pressed={beauty} onClick={() => setBeauty(!beauty)}>
                {IC.beauty}<span>Smooth</span>
              </button>
              <button className="rec-ib lab" type="button" aria-label="Mirror yourself, as in a mirror" aria-pressed={mirror} onClick={() => setMirror(!mirror)}>
                {IC.mirror}<span>Mirror</span>
              </button>
            </div>
          </div>
        </aside>
      )}

      {/* Slow and Fast: hold to use, only while recording */}
      <div className={`rec-pedal l${held === -1 ? ' held' : ''}`} role="button" aria-label="Hold to slow the script"
        onPointerDown={padDown(-1)} onPointerUp={padUp(-1)} onPointerCancel={padUp(-1)} onLostPointerCapture={padUp(-1)} onContextMenu={(e) => e.preventDefault()}>
        <span className="tri" /><small>{live && ps.m < 1 ? `${Math.round(wpm * ps.m)} wpm` : 'Slow'}</small>
        <svg viewBox="0 0 24 24"><path d="M12 18l-6-6 6-6M19 18l-6-6 6-6" /></svg>
      </div>
      <div className={`rec-pedal r${held === 1 ? ' held' : ''}`} role="button" aria-label="Hold to speed the script up"
        onPointerDown={padDown(1)} onPointerUp={padUp(1)} onPointerCancel={padUp(1)} onLostPointerCapture={padUp(1)} onContextMenu={(e) => e.preventDefault()}>
        <span className="tri" /><small>{live && ps.m > 1 ? `${Math.round(wpm * ps.m)} wpm` : 'Fast'}</small>
        <svg viewBox="0 0 24 24"><path d="M5 6l6 6-6 6M12 6l6 6-6 6" /></svg>
      </div>

      {countN > 0 && <div className="rec-count"><span key={countN}>{countN}</span></div>}

      <ScriptEditor open={editing} paras={paras} at={editAt} wpm={wpm} edited={scriptEdited(story.id, story.voice)} written={story.script || ''}
        tsize={tsize} onTsize={setTsize} onDone={(t) => closeEdit(t)} onCancel={() => closeEdit(null)} />

      {sheet?.kind === 'gallery' && (
        <GallerySheet items={items} cur={cur} story={story} onPick={pickFiles} onGo={go} onRemove={removeItem}
          onTrim={(i) => setSheet({ kind: 'trim', id: items[i].id, long: false })} onClose={() => setSheet(null)} />
      )}
      {sheet?.kind === 'trim' && trimItem && (
        <TrimSheet item={trimItem} long={sheet.long} onDone={(s, e) => {
          trimItem.start = s; trimItem.end = e
          trimItem.thumb = trimItem.thumb || thumbOf(trimItem)
          saveGallery(); refreshGallery(); setSheet({ kind: 'gallery' })
        }} />
      )}
      {sheet?.kind === 'take' && (
        <TakeSheet take={sheet.take} story={story} toast={toast}
          onSaved={(t) => markRecorded.mutate({ storyId: story.id, durationSeconds: t.dur, mimeType: t.mime || null, saved: !!t.blob })}
          onRetake={() => { setSheet(null); toast('Take kept. Record another when you’re ready.', 2200) }}
          onDone={onDone} onPosted={onPosted} onClose={() => setSheet(null)} />
      )}
      {sheet?.kind === 'full' && (
        <FullSheet storyId={story.id} then={sheet.then} toast={toast} onCleared={onCleared} onClose={() => setSheet(null)}
          titles={Object.fromEntries((allStories ?? []).map((s) => [s.id, s.headline]))} />
      )}

      <div className={`rec-toast${toastMsg.on ? ' on' : ''}`} role="status">{toastMsg.node}</div>
      <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="rec-file"
        onChange={(e) => { const f = [...(e.target.files ?? [])]; e.target.value = ''; void addFiles(f) }} />

      {practice && <Practice script={script} onClose={() => setPractice(false)} />}
    </div>
  )
}
